// src/Community/UserProfile.js
import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {getUserProfile, getAllUserNames, getFollowers, getFollowing, handleLike} from '../utils/dbUtils';
import profileIcon from '../Icons/profile-menu.png';
import RecipeCard from './RecipeCard';
import './UserProfile.css';
import {useAuth} from '../Auth/AuthContext';
import pencilIcon from '../Icons/lapizgreen.png';
import {useFavoriteRecipes} from '../hooks/useFavoriteRecipes';
import FollowersModal from './FollowersModal'; // Importa el modal
import { useFollowing } from '../context/FollowingContext'; // <-- context global
import { doc, getDoc } from 'firebase/firestore'; // <- nuevo import
import { db } from '../config/firebase';
import addIcon from '../Icons/post recipe gray.png'; // <- NEW: same icon used in CommunityFeed
import { usePlan } from '../context/PlanContext';

const UserProfile = () => {
  const {userId: currentUserId} = useAuth();
  const {userId: paramUserId} = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [favoriteRecipesIds, setFavoriteRecipesIds] = useState([]);
  const [userNames, setUserNames] = useState({});
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);

  // <-- Añadir esto: hook para favoritos del usuario actual (provee addToFavorites/removeFromFavorites)
  const { addToFavorites, removeFromFavorites } = useFavoriteRecipes(currentUserId);
  const { isPremium } = usePlan();

  // NEW: follow state from global context
  const { following: myFollowing, followUser, unfollowUser } = useFollowing();
  const isFollowingProfile = myFollowing.includes(paramUserId);

  // Para MI perfil: usar myFollowing del contexto (tiempo real).
  // Para perfiles ajenos: usar el estado local fetcheado.
  const isOwnProfileEarly = currentUserId && paramUserId && String(currentUserId) === String(paramUserId);
  const displayFollowing = isOwnProfileEarly ? myFollowing : following;

  useEffect(() => {
    const fetchUser = async () => {
      const data = await getUserProfile(paramUserId);
      if (data) {
        setUser(data);

        // Normalizar recetasCompartidas: si vienen como IDs (strings) -> fetch docs
        const rawShared = data.recetasCompartidas || [];
        if (rawShared.length > 0 && typeof rawShared[0] === 'string') {
          const fetched = await Promise.allSettled(
            rawShared.map(id => getDoc(doc(db, 'recetas', id)))
          );
          const recipesFromIds = fetched
            .map((r, i) => {
              if (r.status !== 'fulfilled') {
                console.warn('[skip receta]', rawShared[i], r.reason?.message || r.reason);
                return null;
              }
              const snap = r.value;
              if (!snap.exists()) return null;
              return { id: snap.id, ...snap.data() };
            })
            .filter(Boolean);
          setRecipes(recipesFromIds);
        } else {
          // Soporta formato antiguo (objetos embebidos)
          setRecipes(rawShared || []);
        }

        setFavoriteRecipesIds(
          data.favoriteRecipesIds || data.recetasFavoritas || [],
        );
     }
   };
    fetchUser();
 }, [paramUserId]);

  useEffect(() => {
    const fetchUserNames = async () => {
      const names = await getAllUserNames();
      setUserNames(names);
   };
    fetchUserNames();
 }, []);

  // Fetch followers/following for the profile being viewed
  useEffect(() => {
    if (!paramUserId) return;
    const fetchAllFollowData = async () => {
      try {
        const [followersData, followingData] = await Promise.all([
          getFollowers(paramUserId),
          getFollowing(paramUserId),
        ]);
        setFollowers(followersData);
        setFollowing(followingData);
      } catch (e) {
        console.error('Error cargando datos de follow:', e);
      }
    };
    fetchAllFollowData();
  }, [paramUserId]);

  const handleToggleFollow = async () => {
    if (!currentUserId || !paramUserId) return;
    try {
      if (isFollowingProfile) {
        await unfollowUser(paramUserId);
      } else {
        await followUser(paramUserId);
      }
    } catch (err) {
      console.error('Error toggling follow', err);
    }
  };

  const handleLikeRecipe = async (recipeId) => {
    await handleLike(recipeId, currentUserId, setRecipes);
 };

  if (!user) return <p>Cargando...</p>;

  const isOwnProfile = String(currentUserId).trim() === String(paramUserId).trim();

  return (
    <div className="user-profile">
      <div className="profile-header">
        <img
          src={user?.profileImage || profileIcon}
          alt="Perfil"
          className="profile-image"
        />
        <div className="profile-info">
          <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
            <h2 style={{fontWeight: 'bold', color: '#111', margin: 0}}>{user?.userName || user?.name}</h2>
            {isOwnProfile && (
              <img
                src={pencilIcon}
                alt="Editar Perfil"
                className="edit-profile-pencil"
                onClick={() => navigate('/profile/edit')}
                title="Editar Perfil"
              />
            )}
          </div>

          <div className="profile-stats-row">
            <div className="profile-stat">
              <div className="profile-stat-number">{recipes.length}</div>
              <div className="profile-stat-label">publicaciones</div>
            </div>
            <div
              className="profile-stat"
              onClick={() => setShowFollowersModal(true)}
              style={{cursor: 'pointer'}}
            >
              <div className="profile-stat-number">{followers.length}</div>
              <div className="profile-stat-label">seguidores</div>
            </div>
            <div
              className="profile-stat"
              onClick={() => setShowFollowersModal(true)}
              style={{cursor: 'pointer'}}
            >
              <div className="profile-stat-number">{displayFollowing.length}</div>
              <div className="profile-stat-label">seguidos</div>
            </div>
          </div>

          {/* Plan badge — solo en el perfil propio */}
          {isOwnProfile && (
            <div className="profile-plan-box">
              <span className={`plan-badge ${isPremium ? 'plan-badge--premium' : 'plan-badge--basic'}`}>
                {isPremium ? '⭐ PREMIUM' : 'GRATIS'}
              </span>
              {!isPremium && (
                <button className="plan-upgrade-btn" onClick={() => navigate('/pricing')}>
                  Házte Premium por sólo 4,99€/mes →
                </button>
              )}
            </div>
          )}

          {/* FOLLOW BUTTON: moved BELOW stats and styled via CSS */}
          {!isOwnProfile && (
            <div style={{marginTop: 12, alignSelf: 'flex-start'}}>
              <button
                className={`follow-button ${isFollowingProfile ? 'unfollow' : 'follow'}`}
                onClick={handleToggleFollow}
              >
                {isFollowingProfile ? 'Dejar de Seguir' : 'Seguir'}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="profile-recipes">
        {recipes.length > 0 ? (
          <div className="feed">
            {recipes.map((recipe, idx) => (
              <RecipeCard
                key={recipe?.id || recipe?.objectID || String(recipe) || idx}
                recipe={recipe}
                userId={currentUserId}
                userNames={userNames}
                handleLike={handleLikeRecipe}
                handleAddToFavorites={addToFavorites}
                handleRemoveFromFavorites={removeFromFavorites}
                favoriteRecipesIds={favoriteRecipesIds}
              />
            ))}
          </div>
        ) : (
          <p>Aún no ha compartido ninguna receta.</p>
        )}
      </div>

      {showFollowersModal && (
        <FollowersModal
          followers={followers}
          following={displayFollowing}
          userNames={userNames}
          onClose={() => setShowFollowersModal(false)}
        />
      )}

      {/* NEW: same floating Create Recipe button as in CommunityFeed (only on own profile) */}
      {isOwnProfile && (
        <button
          className="circular-green-btn"
          aria-label="Crear receta"
          onClick={() => navigate('/add-recipe')}
        >
          <img src={addIcon} alt="Crear receta" className="add-recipe-icon" />
        </button>
      )}
    </div>
  );
};

export default UserProfile;